import type { KeyResultProgressModeT, ObjectiveStatusT } from '@portfolio-pm/shared';
import type { ReactNode } from 'react';

/**
 * Shared presentation primitives for the OKR workspace: class-name
 * constants, status/date formatting, progress bar, status badge, and
 * small layout helpers. Extracted from OkrWorkspace.tsx.
 */

export const STATUS_OPTIONS: ObjectiveStatusT[] = ['on_track', 'at_risk', 'off_track'];

export const shellCardClass = 'surface-panel';
export const innerCardClass = 'surface-panel-muted';
export const labelClass = 'eyebrow-label';
export const titleClass = 'text-sm font-semibold text-[color:var(--app-text)]';
export const mutedClass = 'text-xs text-[color:var(--app-text-soft)]';
export const fieldClass = 'ui-input';
export const selectClass = 'ui-select';
export const primaryButtonClass = 'ui-button ui-button-primary h-8 gap-1.5 px-3 text-xs';
export const secondaryButtonClass = 'ui-button ui-button-subtle h-8 gap-1.5 px-3 text-xs';
export const iconButtonClass = 'ui-button ui-button-subtle h-8 w-8 px-0';
export const iconPrimaryButtonClass = 'ui-button ui-button-primary h-8 w-8 px-0';

export function formatStatus(status: ObjectiveStatusT): string {
  return status.replace('_', ' ');
}

export function formatDateInput(value: number | undefined): string {
  if (value === undefined) return '';
  const d = new Date(value);
  const yyyy = d.getUTCFullYear();
  const mm = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const dd = `${d.getUTCDate()}`.padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function parseDateInput(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

export function formatProgressMode(value: KeyResultProgressModeT): string {
  if (value === 'task_rollup') return 'task rollup';
  return value;
}

export function ProgressBar({
  value,
  colorClass = 'bg-[color:var(--app-info)]',
}: {
  value: number;
  colorClass?: string;
}): JSX.Element {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className="progress-track">
      <div
        className={`h-full rounded-full transition-all ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function StatusBadge({ status }: { status: ObjectiveStatusT }): JSX.Element {
  const colorMap: Record<ObjectiveStatusT, string> = {
    on_track: 'ui-chip-success',
    at_risk: 'ui-chip-warning',
    off_track: 'ui-chip-danger',
  };
  return (
    <span className={`ui-chip text-[10px] capitalize ${colorMap[status]}`}>
      {formatStatus(status)}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title: string;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        {eyebrow && <p className={labelClass}>{eyebrow}</p>}
        <h2 className={`mt-1 ${titleClass}`}>{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function IconActionButton({
  label,
  onClick,
  children,
  variant = 'subtle',
}: {
  label: string;
  onClick(): void;
  children: ReactNode;
  variant?: 'subtle' | 'primary';
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={variant === 'primary' ? iconPrimaryButtonClass : iconButtonClass}
    >
      {children}
    </button>
  );
}

export const MOBILE_OKR_QUERY = '(max-width: 767px)';

export function matchesMobileOkr(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_OKR_QUERY).matches;
}
